import React, { useState, useEffect } from 'react';
import { organizationService } from '../../services/organizationService';
import { useLanguage } from '../../contexts/LanguageContext';
import { Eye, Plus, Search } from 'lucide-react';

const OrganizationList = ({ onOrgSelect, onCreateNew }) => {
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const { t } = useLanguage();

    useEffect(() => {
        loadOrganizations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, searchTerm]);

    const loadOrganizations = async () => {
        try {
            setLoading(true);
            const response = await organizationService.getAllOrganizations(currentPage, 20, searchTerm);
            if (response.success) {
                setOrganizations(response.data.organizations || []);
                setTotalPages(response.data.pagination?.pages || 1);
            }
        } catch (error) {
            console.error(t('loadUsersError'), error);
            setOrganizations([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleDateString('tr-TR') : '-';

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <div>{t('loading')}</div>
            </div>
        );
    }

    return (
        <div className="organization-management">
            <div className="page-header">
                <h1>{t('organizationManagement')}</h1>
                <p>{t('organizationManagementDescription')}</p>
            </div>

            <div className="users-content">
                <div className="users-stats">
                    <div className="stat-card">
                        <div className="stat-value">{organizations.length}</div>
                        <div className="stat-label">{t('totalOrganizations')}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-value">
                            {organizations.reduce((sum, o) => sum + (o.seat_count || 0), 0)}
                        </div>
                        <div className="stat-label">{t('totalSeats')}</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-value">
                            {organizations.reduce((sum, o) => sum + (o.occupiedSeats || 0), 0)}
                        </div>
                        <div className="stat-label">{t('occupiedSeats')}</div>
                    </div>
                </div>

                <div className="users-table-container">
                    <div className="table-header">
                        <h2>{t('allOrganizations')}</h2>
                        <div className="table-actions">
                            <div className="search-box">
                                <Search size={18} />
                                <input
                                    type="text"
                                    placeholder={t('searchOrganizations')}
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                            </div>
                            <button className="view-btn" onClick={onCreateNew} title={t('provisionOrganization')}>
                                <Plus size={16} /> {t('provisionOrganization')}
                            </button>
                        </div>
                    </div>

                    <table className="users-table">
                        <thead>
                            <tr>
                                <th>{t('id')}</th>
                                <th>{t('organizationName')}</th>
                                <th>{t('organizationType')}</th>
                                <th>{t('seats')}</th>
                                <th>{t('billingPeriod')}</th>
                                <th>{t('status')}</th>
                                <th>{t('contractEnd')}</th>
                                <th>{t('actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {organizations.map(org => (
                                <tr key={org.id}>
                                    <td>{org.id}</td>
                                    <td>{org.name}</td>
                                    <td>{org.org_type === 'government' ? t('typeGovernment') : t('typePrivate')}</td>
                                    <td>{org.occupiedSeats || 0} / {org.seat_count}</td>
                                    <td>{org.billing_period === 'monthly' ? t('monthly') : t('yearly')}</td>
                                    <td>
                                        <span className={`status-toggle ${org.status === 'active' ? 'active' : 'inactive'}`}>
                                            {org.status === 'active' ? t('active') : t('inactive')}
                                        </span>
                                    </td>
                                    <td>{formatDate(org.ends_at)}</td>
                                    <td>
                                        <div className="user-actions">
                                            <button className="view-btn" onClick={() => onOrgSelect(org)} title={t('viewDetails')}>
                                                <Eye size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {organizations.length === 0 && !loading && (
                        <div className="no-data"><p>{t('noOrganizationsFound')}</p></div>
                    )}

                    <div className="pagination">
                        <div className="pagination-info">{t('page')} {currentPage} / {totalPages}</div>
                        <div className="pagination-controls">
                            <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>{t('previous')}</button>
                            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>{t('next')}</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrganizationList;
